import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { Package, FileCode, User, ExternalLink, Activity, GitBranch, Network, TrendingUp, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, X, Wifi, Clock, Zap, Shield, Eye, Info, Target, Server, Layers } from 'lucide-react';
import api from '../utils/api';
import { AuthContext } from '../index';
import DetailsModal from './modals/DetailsModal';
import NetworkMapModal from './modals/NetworkMapModal';
import ConfidenceSankey from './visualizations/ConfidenceSankey';
import PlatformHealthComparison from './visualizations/PlatformHealthComparison';
import StateTransitionSwimlane from './visualizations/StateTransitionSwimlane';
import EntityTypeSunburst from './visualizations/EntityTypeSunburst';
import DependencyNetworkGraph from './visualizations/DependencyNetworkGraph';
import HealthScoreViolin from './visualizations/HealthScoreViolin';
import TimeToRecoveryChart from './visualizations/TimeToRecoveryChart';
import ConfidenceHealthCorrelation from './visualizations/ConfidenceHealthCorrelation';

const Software = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(true);
  const [allSoftwareData, setAllSoftwareData] = useState([]);
  const [unhealthyDevices, setUnhealthyDevices] = useState([]);
  const [showTroubleshootBox, setShowTroubleshootBox] = useState(false);
  const [troubleshootStream, setTroubleshootStream] = useState('');
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'health-timeline', 'network-graph', 'risk-matrix'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [healthStats, setHealthStats] = useState({
    healthy: 0,
    degraded: 0,
    unhealthy: 0,
    unknown: 0,
    avgScore: 0
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const troubleshootWsRef = useRef(null);
  const streamContentRef = useRef(null);
  
  const { auth } = useContext(AuthContext);
  
  // Check admin status
  useEffect(() => {
    if (auth.isAuthenticated()) {
      auth.getUserInfo().then(info => {
        setIsAdmin(info.isAdmin || false);
      }).catch(() => {
        setIsAdmin(false);
      });
    }
  }, [auth]);

  // Convert to Mountain Time
  const convertToMountainTime = (dateString) => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { date: 'N/A', time: '' };
      const dateStr = date.toLocaleString('en-US', {
        timeZone: 'America/Denver',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
      const timeStr = date.toLocaleString('en-US', {
        timeZone: 'America/Denver',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return { date: dateStr, time: timeStr };
    } catch (error) {
      return { date: 'N/A', time: '' };
    }
  };

  // Fetch data from new API endpoint
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('[Software] Fetching data from UI changes API...');
        const response = await api.getUIChanges();
        const allHealthData = response.items || response.data || response || [];
        
        console.log(`[Software] Loaded ${allHealthData.length} records`);

        if (!Array.isArray(allHealthData) || allHealthData.length === 0) {
          console.warn('[Software] No data received from API');
          setError('No data available.');
          setAllSoftwareData([]);
          setUnhealthyDevices([]);
          setLoading(false);
          return;
        }

        // Calculate health statistics
        let healthy = 0, degraded = 0, unhealthy = 0, unknown = 0;
        let totalScore = 0, scoreCount = 0;

        // Transform data
        const transformedData = allHealthData.map((item, index) => {
          const rels = item.relationships || {};
          const upstream = rels.upstream || [];
          const downstream = rels.downstream || [];
          const totalRels = parseInt(rels.total_relationships) || (upstream.length + downstream.length);

          let healthStatus = 'UNKNOWN';
          let healthScore = 0;

          if (item.main_entity_health && item.main_entity_health.status) {
            healthStatus = item.main_entity_health.status;
            healthScore = parseInt(item.main_entity_health.score) || 0;
            
            // Count health statuses
            if (healthStatus === 'HEALTHY') healthy++;
            else if (healthStatus === 'DEGRADED') degraded++;
            else if (healthStatus === 'UNHEALTHY') unhealthy++;
            else unknown++;
            
            if (healthScore > 0) {
              totalScore += healthScore;
              scoreCount++;
            }
          } else {
            unknown++;
          }

          const dateTime = convertToMountainTime(item.latest_state_start || item.requested_by_date || item.created_at);
          const description = item.short_description || item.u_app_name || item.u_version || item.description || 'Unknown Service';
          const entityType = item.step2_entity_type || item.entity_type || 'SERVICE';
          
          const dynatraceLink = item.step2_dynatrace_link || item.dynatrace_link || null;
          let servicenowLink = item.servicenow_link || null;
          if (!servicenowLink && item.sys_id) {
            servicenowLink = `https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${item.sys_id}`;
          }

          let entity = 'package';
          if (entityType.includes('SERVICE') || entityType.includes('APPLICATION')) entity = 'package';
          else if (entityType.includes('FILE') || entityType.includes('DATABASE')) entity = 'file';
          else entity = 'user';

          let stateColor = 'text-text-secondary';
          if (item.current_state === 'Closed Complete') stateColor = 'text-emerald-400';
          else if (item.current_state === 'Move to Prod') stateColor = 'text-blue-400';
          else if (item.current_state === 'In Progress' || item.current_state === 'Prod Verification') stateColor = 'text-amber-400';

          return {
            id: index + 1,
            date: dateTime.date,
            time: dateTime.time,
            entity: entity,
            entityType: entityType,
            changeNumber: item.change_number || item.id || 'N/A',
            description: description,
            author: `by ${typeof item.opened_by === 'object' ? (item.opened_by?.display_value || item.opened_by?.value || 'System') : (item.opened_by || 'System')}`,
            state: item.current_state || 'Unknown',
            stateColor: stateColor,
            health: healthStatus,
            healthScore: healthScore,
            dynatrace: !!dynatraceLink,
            dynatraceLink: dynatraceLink,
            servicenowLink: servicenowLink,
            dependencies: totalRels > 0 ? `${totalRels} dependenc${totalRels === 1 ? 'y' : 'ies'}` : 'No dependencies',
            totalRelationships: totalRels,
            confidenceScore: item.step2_confidence_score || 0,
            healthCheckCount: item.health_check_count || 0,
            healthCheckHistory: item.health_check_history || [],
            raw_data: item
          };
        });

        console.log(`[Software] Transformed ${transformedData.length} items`);
        setAllSoftwareData(transformedData);

        // Set health statistics
        setHealthStats({
          healthy,
          degraded,
          unhealthy,
          unknown,
          avgScore: scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0
        });

        // Filter unhealthy items for compact display (max 5)
        const unhealthyItems = transformedData
          .filter(item => item.health === 'DEGRADED' || item.health === 'UNHEALTHY')
          .slice(0, 5)
          .map((item, idx) => ({
            id: idx + 1,
            changeNumber: item.changeNumber,
            description: item.description,
            health: item.health,
            healthScore: item.healthScore,
            severity: item.health === 'UNHEALTHY' ? 'critical' : 'warning',
            timestamp: `${item.date} ${item.time}`
          }));
        console.log(`[Software] Found ${unhealthyItems.length} unhealthy items`);
        setUnhealthyDevices(unhealthyItems);

      } catch (error) {
        console.error('[Software] Error loading software data:', error);
        setError(error.message || 'Failed to load data.');
        setAllSoftwareData([]);
        setUnhealthyDevices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const dismissDevice = (id) => {
    setUnhealthyDevices(prev => prev.filter(device => device.id !== id));
  };

  // Troubleshoot WebSocket functionality
  const addToStream = (text) => {
    setTroubleshootStream(prev => {
      const newText = prev ? prev + '\n' + text : text;
      setTimeout(() => {
        if (streamContentRef.current) {
          streamContentRef.current.scrollTop = streamContentRef.current.scrollHeight;
        }
      }, 0);
      return newText;
    });
  };

  const startTroubleshoot = async () => {
    try {
      setShowTroubleshootBox(true);
      setTroubleshootStream('');
      addToStream('⏳ Connecting to troubleshooting service...');

      const WS_URL = 'wss://1r5x85vspl.execute-api.us-west-2.amazonaws.com/production';
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        console.log('[Troubleshoot] Connected');
        addToStream('🔌 Connected! Starting scan...');
        ws.send(JSON.stringify({ action: 'troubleshoot' }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          let message = '';
          switch(data.type) {
            case 'analysis_start':
              message = '🚀 ' + (data.message || 'Starting analysis...');
              break;
            case 'item_start':
              message = '\n📋 ' + (data.message || 'Starting item');
              break;
            case 'item_progress':
            case 'diagnostic_step':
            case 'data_collected':
            case 'metric_collection':
              message = '⏳ ' + (data.message || 'Processing...');
              break;
            case 'ai_thinking':
              message = '🤖 ' + (data.agent || 'AI') + ': ' + (data.message || 'Analyzing...');
              break;
            case 'reasoning_update':
              const reasoning = data.reasoning || data.full_reasoning?.output || '';
              message = '\n💭 ' + (data.agent || 'AI') + ':\n' + reasoning.substring(0, 200);
              break;
            case 'item_result':
              const changeNum = data.data?.change_number || 'Unknown';
              const status = data.data?.health_status || 'UNKNOWN';
              message = '\n✅ ' + changeNum + ' - ' + status;
              break;
            case 'complete':
              message = '\n\n✅ ' + (data.message || 'Scan complete!');
              break;
            case 'error':
              message = '\n❌ ERROR: ' + (data.message || 'Unknown error');
              break;
            default:
              if (data.message) message = data.message;
          }
          if (message) addToStream(message);
        } catch (error) {
          console.error('[Troubleshoot] Parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[Troubleshoot] Error:', error);
        addToStream('\n❌ Connection error occurred');
      };

      ws.onclose = () => {
        console.log('[Troubleshoot] Connection closed');
      };

      troubleshootWsRef.current = ws;
    } catch (error) {
      console.error('[Troubleshoot] Error:', error);
      addToStream('\n❌ Connection failed: ' + error.message);
    }
  };

  const closeTroubleshootBox = () => {
    if (troubleshootWsRef.current) {
      troubleshootWsRef.current.close();
      troubleshootWsRef.current = null;
    }
    setShowTroubleshootBox(false);
    setTroubleshootStream('');
  };

  useEffect(() => {
    return () => {
      if (troubleshootWsRef.current) {
        troubleshootWsRef.current.close();
      }
    };
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="opacity-50" />;
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = allSoftwareData.filter(item =>
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.changeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.state.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === 'date') {
          const aDateStr = a.date + ' ' + a.time;
          const bDateStr = b.date + ' ' + b.time;
          const aDate = new Date(aDateStr);
          const bDate = new Date(bDateStr);
          aVal = isNaN(aDate.getTime()) ? 0 : aDate.getTime();
          bVal = isNaN(bDate.getTime()) ? 0 : bDate.getTime();
        }
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [searchTerm, sortConfig, allSoftwareData]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);

  const getEntityIcon = (entity) => {
    switch(entity) {
      case 'package': return <Package size={20} className="text-blue-400" />;
      case 'file': return <FileCode size={20} className="text-pink-500" />;
      case 'user': return <User size={20} className="text-blue-400" />;
      default: return <Package size={20} className="text-blue-400" />;
    }
  };

  const getHealthIcon = (health) => {
    switch(health) {
      case 'HEALTHY':
        return <Activity size={16} className="text-green-500" />;
      case 'DEGRADED':
        return <Activity size={16} className="text-yellow-500" />;
      case 'UNHEALTHY':
        return <Activity size={16} className="text-red-500" />;
      case 'PENDING':
        return <Activity size={16} className="text-blue-400" />;
      default:
        return <Activity size={16} className="text-text-muted" />;
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#eab308'; // yellow
    if (score >= 30) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  // Health Timeline Heatmap Component
  const HealthTimelineHeatmap = () => {
    const heatmapData = filteredAndSortedData.slice(0, 20).map(item => {
      const history = item.healthCheckHistory || [];
      return {
        changeNumber: item.changeNumber,
        description: item.description.substring(0, 30),
        healthChecks: history.slice(-10).map(check => ({
          timestamp: check.timestamp,
          score: check.main_health?.score || 0,
          status: check.main_health?.status || 'UNKNOWN'
        }))
      };
    });

    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-xl font-bold text-blue-400 mb-4">Health Timeline Heatmap</h3>
        <div className="space-y-2">
          {heatmapData.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className="w-32 text-xs text-text-secondary truncate" title={item.changeNumber}>
                {item.changeNumber}
              </div>
              <div className="flex-1 flex gap-1">
                {item.healthChecks.length > 0 ? (
                  item.healthChecks.map((check, checkIdx) => (
                    <div
                      key={checkIdx}
                      className="flex-1 h-8 rounded"
                      style={{
                        backgroundColor: getHealthColor(check.score),
                        boxShadow: `0 0 10px ${getHealthColor(check.score)}40`
                      }}
                      title={`Score: ${check.score} - ${check.status}`}
                    />
                  ))
                ) : (
                  <div className="flex-1 h-8 rounded bg-surface-2" title="No health data" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Deployment Risk Matrix Component
  const DeploymentRiskMatrix = () => {
    const riskData = filteredAndSortedData.map(item => ({
      changeNumber: item.changeNumber,
      confidenceScore: item.confidenceScore || 50,
      complexity: item.totalRelationships || 0,
      healthScore: item.healthScore || 0,
      health: item.health
    }));

    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-xl font-bold text-blue-400 mb-4">Deployment Risk Matrix</h3>
        <div className="relative h-96 border border-border rounded">
          {/* Quadrant labels */}
          <div className="absolute top-2 left-2 text-xs text-blue-400">High Risk</div>
          <div className="absolute top-2 right-2 text-xs text-amber-400">Moderate Risk</div>
          <div className="absolute bottom-2 left-2 text-xs text-blue-400">Low Risk</div>
          <div className="absolute bottom-2 right-2 text-xs text-emerald-400">Safe</div>
          
          {/* Scatter plot */}
          <div className="relative w-full h-full p-8">
            {riskData.map((item, idx) => {
              const x = (item.confidenceScore / 100) * 100;
              const y = 100 - ((item.complexity / Math.max(...riskData.map(d => d.complexity), 1)) * 100);
              const size = Math.max(8, Math.min(20, item.healthScore / 5));
              
              return (
                <div
                  key={idx}
                  className="absolute rounded-full cursor-pointer transition-all hover:scale-150"
                  style={{
                    left: `${x}%`,
                    top: `${y}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    backgroundColor: getHealthColor(item.healthScore),
                    boxShadow: `0 0 15px ${getHealthColor(item.healthScore)}80`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  title={`${item.changeNumber}\nConfidence: ${item.confidenceScore}%\nComplexity: ${item.complexity}\nHealth: ${item.healthScore}`}
                />
              );
            })}
          </div>
          
          {/* Axes */}
          <div className="absolute bottom-0 left-0 right-0 text-center text-xs text-text-muted pb-1">
            Confidence Score →
          </div>
          <div className="absolute top-0 bottom-0 left-0 flex items-center text-xs text-text-muted pl-1"
               style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            Complexity →
          </div>
        </div>
      </div>
    );
  };

  // Real-Time Health Pulse Dashboard
  const HealthPulseDashboard = () => {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-xl font-bold text-blue-400 mb-4">Real-Time Health Pulse</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredAndSortedData.slice(0, 12).map((item, idx) => (
            <div
              key={idx}
              className="relative p-4 rounded-lg border transition-all hover:scale-105 cursor-pointer"
              style={{
                backgroundColor: '#09090b',
                borderColor: getHealthColor(item.healthScore),
                boxShadow: `0 0 20px ${getHealthColor(item.healthScore)}40`,
                animation: item.health === 'UNHEALTHY' ? 'pulse 2s infinite' : 'none'
              }}
            >
              <div className="text-xs text-text-secondary truncate mb-2" title={item.changeNumber}>
                {item.changeNumber}
              </div>
              <div className="text-2xl font-bold mb-2" style={{ color: getHealthColor(item.healthScore) }}>
                {item.healthScore}
              </div>
              <div className="text-xs text-text-muted">{item.health}</div>
              
              {/* Mini sparkline */}
              <div className="mt-2 flex gap-0.5 h-6 items-end">
                {(item.healthCheckHistory || []).slice(-8).map((check, i) => {
                  const score = check.main_health?.score || 0;
                  const height = (score / 100) * 100;
                  return (
                    <div
                      key={i}
                      className="flex-1 rounded-t"
                      style={{
                        height: `${height}%`,
                        backgroundColor: getHealthColor(score),
                        opacity: 0.7
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6 relative">
      {/* Troubleshooting Stream Box */}
      {showTroubleshootBox && (
        <div className="fixed top-20 right-5 w-96 max-h-[600px] bg-surface border border-red-500/50 rounded-xl shadow-2xl z-50 overflow-hidden"
             >
          <div className="p-3 bg-gradient-to-r from-red-600 to-red-700 flex justify-between items-center cursor-pointer"
               onClick={() => setShowTroubleshootBox(!showTroubleshootBox)}>
            <div className="flex items-center gap-2 text-text-primary font-semibold">
              <Wifi size={16} />
              <span>AI Troubleshooting</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeTroubleshootBox();
              }}
              className="text-text-primary hover:text-red-200 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div
            ref={streamContentRef}
            className="p-4 max-h-[540px] overflow-y-auto font-mono text-xs leading-relaxed text-text-primary"
            style={{ fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace" }}
          >
            <span className="whitespace-pre-wrap">{troubleshootStream}</span>
            <span className="inline-block w-2 h-3.5 bg-red-500 ml-1 animate-pulse"></span>
          </div>
        </div>
      )}

      {/* Health Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-surface rounded-lg border border-lime-accent/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <Shield size={20} className="text-emerald-400" />
            <span className="text-2xl font-bold text-emerald-400">{healthStats.healthy}</span>
          </div>
          <div className="text-xs text-text-secondary">Healthy</div>
        </div>
        
        <div className="bg-surface rounded-lg border border-yellow-accent/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle size={20} className="text-amber-400" />
            <span className="text-2xl font-bold text-amber-400">{healthStats.degraded}</span>
          </div>
          <div className="text-xs text-text-secondary">Degraded</div>
        </div>
        
        <div className="bg-surface rounded-lg border border-red-500/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <X size={20} className="text-red-500" />
            <span className="text-2xl font-bold text-red-500">{healthStats.unhealthy}</span>
          </div>
          <div className="text-xs text-text-secondary">Unhealthy</div>
        </div>
        
        <div className="bg-surface rounded-lg border border-gray-500/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <Eye size={20} className="text-text-muted" />
            <span className="text-2xl font-bold text-text-muted">{healthStats.unknown}</span>
          </div>
          <div className="text-xs text-text-secondary">Unknown</div>
        </div>
        
        <div className="bg-surface rounded-lg border border-blue-500/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <Zap size={20} className="text-blue-400" />
            <span className="text-2xl font-bold text-blue-400">{healthStats.avgScore}</span>
          </div>
          <div className="text-xs text-text-secondary">Avg Score</div>
        </div>
      </div>

      {/* Compact Unhealthy Devices List */}
      {unhealthyDevices.length > 0 && (
        <div className="bg-surface rounded-lg border border-blue-500/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-blue-400 animate-pulse" />
              <h3 className="text-sm font-bold text-blue-400">
                {unhealthyDevices.length} Unhealthy Service{unhealthyDevices.length > 1 ? 's' : ''}
              </h3>
            </div>
          </div>
          
          <div className="space-y-2">
            {unhealthyDevices.map((device) => (
              <div key={device.id}
                   className="flex items-center justify-between p-2 rounded bg-surface-2/50 border border-border/40 hover:border-blue-500/30 transition-all group text-sm">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    device.severity === 'critical' ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
                  }`} />
                  <span className="font-semibold text-text-primary truncate">{device.changeNumber}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                    device.severity === 'critical' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {device.healthScore}
                  </span>
                  <span className="text-text-muted truncate text-xs">{device.description.substring(0, 30)}...</span>
                </div>
                
                <button
                  onClick={() => dismissDevice(device.id)}
                  className="p-1 rounded text-text-muted hover:text-blue-400 transition-all opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header with View Mode Selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-blue-400">
          Software Changes
        </h1>
        
        <div className="flex items-center gap-4 w-full sm:w-auto">
          {/* View Mode Selector */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'table'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Table View"
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('health-timeline')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'health-timeline'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Health Timeline"
            >
              <Clock size={16} />
            </button>
            <button
              onClick={() => setViewMode('risk-matrix')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'risk-matrix'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Risk Matrix"
            >
              <TrendingUp size={16} />
            </button>
            <button
              onClick={() => setViewMode('health-pulse')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'health-pulse'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Health Pulse"
            >
              <Activity size={16} />
            </button>
            <button
              onClick={() => setViewMode('confidence-sankey')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'confidence-sankey'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Confidence Flow"
            >
              <Target size={16} />
            </button>
            <button
              onClick={() => setViewMode('platform-comparison')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'platform-comparison'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Platform Health"
            >
              <Server size={16} />
            </button>
            <button
              onClick={() => setViewMode('state-swimlane')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'state-swimlane'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="State Timeline"
            >
              <GitBranch size={16} />
            </button>
            <button
              onClick={() => setViewMode('entity-sunburst')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'entity-sunburst'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Entity Distribution"
            >
              <Layers size={16} />
            </button>
            <button
              onClick={() => setViewMode('network-graph')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'network-graph'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Dependency Network"
            >
              <Network size={16} />
            </button>
            <button
              onClick={() => setViewMode('violin-plot')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'violin-plot'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Health Distribution"
            >
              <TrendingUp size={16} />
            </button>
            <button
              onClick={() => setViewMode('recovery-chart')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'recovery-chart'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Time to Recovery"
            >
              <Clock size={16} />
            </button>
            <button
              onClick={() => setViewMode('correlation')}
              className={`px-3 py-2 rounded text-sm font-semibold transition-all ${
                viewMode === 'correlation'
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400'
              }`}
              title="Confidence vs Health"
            >
              <Target size={16} />
            </button>
          </div>

          {/* Troubleshoot Button - Admin Only */}
          {isAdmin && (
            <div className="flex flex-col items-center">
              <button
                onClick={startTroubleshoot}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-text-primary rounded-lg font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                
              >
                <Wifi size={18} />
                Troubleshoot
              </button>
              <span className="text-xs text-text-muted mt-1">(Admin Function Only)</span>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative flex-1 sm:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              placeholder="Search changes..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-gray-500 focus:outline-none focus:border-blue-500/60 transition-all"
            />
          </div>
        </div>
      </div>

      {/* Conditional View Rendering */}
      {loading ? (
        <div className="bg-surface rounded-lg border border-border p-12 text-center">
          <div className="text-blue-400 text-lg">Loading data...</div>
        </div>
      ) : error ? (
        <div className="bg-surface rounded-lg border border-red-500/40 p-12 text-center">
          <div className="text-red-400 mb-2">Error loading data</div>
          <div className="text-sm text-text-muted mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-400 rounded hover:bg-blue-500/30 transition-all text-sm"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'health-timeline' && <HealthTimelineHeatmap />}
          {viewMode === 'risk-matrix' && <DeploymentRiskMatrix />}
          {viewMode === 'health-pulse' && <HealthPulseDashboard />}
          {viewMode === 'confidence-sankey' && <ConfidenceSankey data={filteredAndSortedData} />}
          {viewMode === 'platform-comparison' && <PlatformHealthComparison data={filteredAndSortedData} />}
          {viewMode === 'state-swimlane' && <StateTransitionSwimlane data={filteredAndSortedData} />}
          {viewMode === 'entity-sunburst' && <EntityTypeSunburst data={filteredAndSortedData} />}
          {viewMode === 'network-graph' && <DependencyNetworkGraph data={filteredAndSortedData} />}
          {viewMode === 'violin-plot' && <HealthScoreViolin data={filteredAndSortedData} />}
          {viewMode === 'recovery-chart' && <TimeToRecoveryChart data={filteredAndSortedData} />}
          {viewMode === 'correlation' && <ConfidenceHealthCorrelation data={filteredAndSortedData} />}
          {viewMode === 'table' && (
            <div className="bg-surface rounded-lg border border-border overflow-hidden relative"
                 >
              
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('date')}>
                        <div className="flex items-center gap-1">
                          Date (MT)
                          {getSortIcon('date')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('entity')}>
                        <div className="flex items-center gap-1">
                          Entity
                          {getSortIcon('entity')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('changeNumber')}>
                        <div className="flex items-center gap-1">
                          Change #
                          {getSortIcon('changeNumber')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('description')}>
                        <div className="flex items-center gap-1">
                          Description
                          {getSortIcon('description')}
                        </div>
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('state')}>
                        <div className="flex items-center gap-1">
                          State
                          {getSortIcon('state')}
                        </div>
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Health
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Dynatrace
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider cursor-pointer hover:text-blue-400 transition-colors" onClick={() => handleSort('dependencies')}>
                        <div className="flex items-center gap-1">
                          Dependencies
                          {getSortIcon('dependencies')}
                        </div>
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Network Map
                      </th>
                      <th className="text-center p-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">
                        Metrics
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-8 text-center text-text-secondary">
                          No data found
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item, index) => (
                        <tr key={item.id || index} 
                            className="border-b border-border/30 hover:bg-surface-2/50 transition-all group"
                            >
                          {/* Date */}
                          <td className="p-4">
                            <div className="text-sm text-text-secondary font-mono">
                              <div>{item.date}</div>
                              <div className="text-xs text-text-muted">{item.time}</div>
                            </div>
                          </td>
                          
                          {/* Entity Icon */}
                          <td className="p-4">
                            <div className="flex items-center justify-center w-10 h-10 rounded bg-surface-2 border border-border/60">
                              {getEntityIcon(item.entity)}
                            </div>
                          </td>
                          
                          {/* Change Number */}
                          <td className="p-4">
                            {item.servicenowLink ? (
                              <a
                                href={item.servicenowLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-1 px-3 py-1 rounded bg-blue-500/20 border border-blue-500/40 text-blue-400 text-sm font-semibold hover:bg-blue-500/30 transition-all"
                              >
                                <span>{item.changeNumber}</span>
                                <ExternalLink size={12} />
                              </a>
                            ) : (
                              <div className="inline-flex items-center space-x-1 px-3 py-1 rounded bg-blue-500/20 border border-blue-500/40 text-blue-400 text-sm font-semibold">
                                <span>{item.changeNumber}</span>
                              </div>
                            )}
                          </td>
                          
                          {/* Description */}
                          <td className="p-4">
                            <div className="text-sm text-text-primary font-medium max-w-xs truncate" title={item.description}>
                              {item.description}
                            </div>
                            <div className="text-xs text-text-muted mt-0.5">{item.author}</div>
                          </td>
                          
                          {/* State */}
                          <td className="p-4">
                            <span className={`text-sm font-semibold ${item.stateColor}`}>
                              {item.state}
                            </span>
                          </td>
                          
                          {/* Health */}
                          <td className="p-4 text-center">
                            <div className="inline-flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-blue-500/40"
                                 title={`${item.health} - Score: ${item.healthScore}`}>
                              {getHealthIcon(item.health)}
                            </div>
                          </td>
                          
                          {/* Dynatrace */}
                          <td className="p-4 text-center">
                            {item.dynatraceLink ? (
                              <a
                                href={item.dynatraceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-lime-accent/40 hover:bg-lime-accent/10 transition-all"
                                title="View in Dynatrace"
                              >
                                <GitBranch size={16} className="text-emerald-400" />
                              </a>
                            ) : (
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-border/60 opacity-40">
                                <GitBranch size={16} className="text-text-muted" />
                              </div>
                            )}
                          </td>
                          
                          {/* Dependencies */}
                          <td className="p-4">
                            <span className="text-sm text-text-muted">{item.dependencies}</span>
                          </td>
                          
                          {/* Network Map */}
                          <td className="p-4 text-center">
                            <button 
                              onClick={() => {
                                setSelectedItem(item);
                                setShowNetworkModal(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-blue-500/40 hover:bg-blue-500/10 transition-all cursor-pointer"
                              title="View Network Map">
                              <Network size={16} className="text-blue-400" />
                            </button>
                          </td>
                          
                          {/* Metrics */}
                          <td className="p-4 text-center">
                            <button 
                              onClick={() => {
                                setSelectedItem(item);
                                setShowDetailsModal(true);
                              }}
                              className="inline-flex items-center justify-center w-8 h-8 rounded bg-surface-2 border border-blue-500/40 hover:bg-blue-500/10 transition-all cursor-pointer"
                              title="View Details & Metrics">
                              <Info size={16} className="text-blue-400" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border/50">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <span>Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    className="px-2 py-1 bg-surface border border-border rounded text-text-primary focus:outline-none focus:border-blue-500/60"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>of {filteredAndSortedData.length} entries</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronsLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (totalPages <= 7 || page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 rounded text-sm font-semibold transition-all ${
                              currentPage === page
                                ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                                : 'bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40'
                            }`}
                            style={currentPage === page ? { boxShadow: '0 0 15px rgba(0, 229, 255, 0.2)' } : {}}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="text-text-muted">...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded bg-surface-2 border border-border/60 text-text-secondary hover:text-blue-400 hover:border-blue-500/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronsRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showDetailsModal && selectedItem && (
        <DetailsModal 
          item={selectedItem} 
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedItem(null);
          }} 
        />
      )}

      {showNetworkModal && selectedItem && (
        <NetworkMapModal 
          item={selectedItem} 
          onClose={() => {
            setShowNetworkModal(false);
            setSelectedItem(null);
          }} 
        />
      )}
    </div>
  );
};

export default Software;
